/* ==========================================================================
   FILE: services/auth.service.js
   IAMONEAI - Authentication Service
   Login, register, verify, logout
   ========================================================================== */

import { client } from '../api';

// ==========================================================================
// CACHE SETTINGS
// ==========================================================================

const CACHE_TTL = {
  identity: 300000, // 5 minutes
};

// ==========================================================================
// AUTH SERVICE
// ==========================================================================

const authService = {
  /**
   * Send verification code to email
   * @param {string} email 
   * @param {string} recaptchaToken 
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async sendCode(email, recaptchaToken = null) {
    const body = { email };
    if (recaptchaToken) body.recaptchaToken = recaptchaToken;

    return client.post('/verify/send-code', body, {
      cache: false,
      retry: false, // Don't retry auth requests
    });
  },

  /**
   * Verify email code
   * @param {string} email 
   * @param {string} code 
   * @returns {Promise<{success: boolean, verified?: boolean, message?: string}>}
   */
  async verifyCode(email, code) {
    return client.post('/verify/check', { email, code }, {
      cache: false,
      retry: false,
    });
  },

  /**
   * Register new identity
   * @param {Object} userData
   * @param {string} userData.firstName
   * @param {string} userData.lastName
   * @param {string} userData.email
   * @param {string} userData.birthDate
   * @param {string} userData.wallet - Optional
   * @returns {Promise<{success: boolean, iin?: string, message?: string}>}
   */
  async register(userData) {
    return client.post('/identity/register', userData, {
      cache: false,
      retry: false,
      priority: 'high',
    });
  },

  /**
   * Lookup identity by IIN (login)
   * @param {string} iin 
   * @returns {Promise<{success: boolean, identity?: Object}>}
   */
  async lookup(iin) {
    // First get brain to verify IIN exists and get user info
    const brainData = await client.get(`/brain/${iin}`, {
      cache: true,
      cacheTTL: CACHE_TTL.identity,
    });

    if (brainData.success && brainData.brain) {
      const owner = brainData.brain.metadata?.owner || 'User';
      const email = brainData.brain.metadata?.email;
      
      return {
        success: true,
        identity: {
          iin,
          owner,
          email,
          blockchainAnchored: brainData.brain.blockchainAnchored || false,
        },
        brain: brainData.brain,
      };
    }

    throw new Error('Identity not found');
  },

  /**
   * Verify identity with security question
   * @param {string} iin 
   * @param {string} securityAnswer 
   * @returns {Promise<{success: boolean, verified?: boolean}>}
   */
  async verify(iin, securityAnswer) {
    return client.post('/identity/verify', { iin, securityAnswer }, {
      cache: false,
      retry: false,
    });
  },

  /**
   * Clear cached identity data on logout
   * @param {string} iin 
   */
  clearCache(iin) {
    client.clearCache();
  },
};

// ==========================================================================
// EXPORTS
// ==========================================================================

export { authService };
export default authService;